import SuperJSON from "superjson";
import { createStore } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { OperationsRoutes } from "@/operations-routes";

/**
 * Standalone, persisted store for the `/operations` shell, deliberately
 * independent of `ui-store` so operations can be lifted into its own portal
 * later. Holds single-pane tab state plus the Operations Client's workspace selection;
 * the cross-cutting prefs (theme, keybinds) stay in `ui-store` and are read from
 * there. Cleared on logout via {@link reset}.
 *
 * `currentWorkspaceId` lives HERE rather than in `ui-store` because the
 * Operations Client offers only the workspaces a tool can be opened in, so a
 * single shared selection would let one client re-point the other.
 */

export type OpsTab = {
	type: OperationsRoutes["type"];
	params: OperationsRoutes["params"];
};

type OpsUIState = {
	tabs: OpsTab[];
	activeTabIndex: number;
	sidebarCollapsed: boolean;
	currentWorkspaceId: string | null;
};

type OpsUIActions = {
	/** Open the tab for `to`, or focus it if already open (tools are singletons). */
	navigate: (to: OperationsRoutes) => void;
	setActiveTab: (index: number) => void;
	closeTab: (index: number) => void;
	setSidebarCollapsed: (collapsed: boolean) => void;
	setCurrentWorkspaceId: (workspaceId: string | null) => void;
	reset: () => void;
};

export type OpsUIStore = OpsUIState & OpsUIActions;

const getInitialState = (): OpsUIState => ({
	tabs: [],
	activeTabIndex: 0,
	sidebarCollapsed: false,
	currentWorkspaceId: null,
});

const isSameTab = (a: OpsTab, b: OpsTab) =>
	a.type === b.type && JSON.stringify(a.params) === JSON.stringify(b.params);

const customStorage = {
	getItem: (name: string) => {
		const data = localStorage.getItem(name);
		if (!data) return null;
		try {
			return SuperJSON.parse(data) as { state: OpsUIState; version?: number };
		} catch {
			localStorage.removeItem(name);
			return null;
		}
	},
	setItem: (name: string, value: unknown) => {
		localStorage.setItem(name, SuperJSON.stringify(value));
	},
	removeItem: (name: string) => {
		localStorage.removeItem(name);
	},
} as const;

export const opsUiStore = createStore<OpsUIStore>()(
	persist(
		immer((set) => ({
			...getInitialState(),
			navigate: ({ type, params }) => {
				set((state) => {
					const newTab = { type, params } as OpsTab;
					const existing = state.tabs.findIndex((t) => isSameTab(t, newTab));
					if (existing !== -1) {
						state.activeTabIndex = existing;
					} else {
						state.tabs.push(newTab);
						state.activeTabIndex = state.tabs.length - 1;
					}
				});
			},
			setActiveTab: (index) => {
				set((state) => {
					if (index >= 0 && index < state.tabs.length) {
						state.activeTabIndex = index;
					}
				});
			},
			closeTab: (index) => {
				set((state) => {
					if (index < 0 || index >= state.tabs.length) return;
					state.tabs.splice(index, 1);
					if (state.activeTabIndex >= state.tabs.length) {
						state.activeTabIndex = Math.max(0, state.tabs.length - 1);
					} else if (index < state.activeTabIndex) {
						state.activeTabIndex -= 1;
					}
				});
			},
			setSidebarCollapsed: (collapsed) => {
				set((state) => {
					state.sidebarCollapsed = collapsed;
				});
			},
			setCurrentWorkspaceId: (workspaceId) => {
				set((state) => {
					state.currentWorkspaceId = workspaceId;
				});
			},
			reset: () => {
				set(getInitialState());
			},
		})),
		{
			name: "elaso-ops-ui-store",
			storage: customStorage as any,
			partialize: (state) => ({
				tabs: state.tabs,
				activeTabIndex: state.activeTabIndex,
				sidebarCollapsed: state.sidebarCollapsed,
				currentWorkspaceId: state.currentWorkspaceId,
			}),
		}
	)
);
